import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { uniq, trim } from 'lodash-es';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getScoresWithPoints, getAllDivisions, getAllCategories, getFlagSrc } from '../../utils/scoring';

const PLACE_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];

export default function FlagsPodium() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const [mode, setMode] = useState('countries');
  const [groups, setGroups] = useState(null);
  const [topShooters, setTopShooters] = useState(null);

  useEffect(() => {
    if (!match) return;

    Promise.all([
      api.getScores(match.id),
      api.getShooters(match.id),
      match.stages?.length > 0 ? api.getStages(match.stages.join(',')) : Promise.resolve({ data: [] }),
    ]).then(([scoresRes, shootersRes, stagesRes]) => {
      const scores = scoresRes.data?.scores || scoresRes.data || [];
      const shooters = (shootersRes.data || []).filter((s) => !s.cancelledParticipation);
      const stages = stagesRes.data || [];

      const { scoresOverall } = getScoresWithPoints({
        scores, shooters, stages, mode: match.scoreMode,
      });

      const divisions = getAllDivisions({ matchType: match.type });
      const categories = getAllCategories({ matchType: match.type });

      const top = [];
      divisions.forEach((division) => {
        categories.forEach((category) => {
          const s = scoresOverall.filter((so) =>
            so.division === division && (category === 'regular' || so.category === category)
          );
          if (s.length < 3) return;
          for (let i = 0; i < Math.min(3, s.length); i++) {
            const shooter = shooters.find((ss) => ss.publicId === s[i].shooterPublicId);
            top.push({
              place: i + 1,
              division,
              category,
              shooterPublicId: s[i].shooterPublicId,
              shooterName: s[i].shooterName,
              club: shooter?.club,
            });
          }
        });
      });

      let $mode = 'countries';
      let $groups = uniq(top.map((ts) => ts.shooterPublicId.split('|')[0]));
      if ($groups.length <= 1) {
        $mode = 'clubs';
        $groups = uniq(top.map((ts) => trim(ts.club)));
      }

      setMode($mode);
      setGroups($groups);
      setTopShooters(top);
    });
  }, [match?.id]);

  if (match.scoresPublished === 'verify-only') return null;

  if (!groups || !topShooters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', px: 2, py: 2 }}>
      {groups.map((group) => (
        <Box key={group} sx={{ width: 300, m: 1.5 }}>
          {mode === 'countries' && (
            <img
              alt={group}
              src={getFlagSrc({ countryCode: group, size: 256 })}
              style={{ width: '100%' }}
            />
          )}
          {mode === 'clubs' && (
            <Typography variant="h5" sx={{ textAlign: 'center', mb: 1 }}>
              {group}
              {' '}
              ({topShooters.filter((ts) => trim(ts.club) === group).length})
            </Typography>
          )}
          {topShooters.map((ts) => {
            if (mode === 'countries' && !ts.shooterPublicId.startsWith(group + '|') && ts.shooterPublicId.split('|')[0] !== group) return null;
            if (mode === 'clubs' && trim(ts.club) !== group) return null;
            return (
              <Box key={`${ts.shooterPublicId}-${ts.division}-${ts.category}`} sx={{ mt: 0.75, display: 'flex', alignItems: 'flex-start' }}>
                <EmojiEventsIcon sx={{ color: PLACE_COLORS[ts.place - 1], fontSize: 36, mr: 0.75 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1">
                    {ts.shooterPublicId.split('|')[1]}
                    &nbsp;|&nbsp;
                    {ts.shooterName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`divisions.${ts.division}`, ts.division)}
                    &nbsp;-&nbsp;
                    {t(`categories.${ts.category}`, ts.category)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
